import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchTournament,
    fetchParticipants,
    fetchLeagueConfig,
    fetchLeagueView,
    saveLeagueConfig,
    saveLeagueGroups,
    assignPlayersToGroups,
    startLeague,
    advanceLeagueRound,
    changeLeagueRoundEndDate,
    addLeaguePlayer,
    modifyLeagueGroups,
    registerTournamentParticipantBySupervisor,
    rollbackLeagueRound,
    adminFreezePlayer,
    adminUnfreezePlayer,
    sendLeagueActivityReminders
} from '../../api/api';
import CustomModal from '../../UI/Modal/CustomModal';
import Spinner from '../Spinner/Spinner';
import styles from './AdminLeagueManagement.module.css';

const AdminLeagueManagement = () => {
    const { tournamentId } = useParams();
    const navigate = useNavigate();
    const [tournament, setTournament] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [config, setConfig] = useState({
        scoringSystem: 'CLASSIC',
        promotedPerGroup: 1,
        relegatedPerGroup: 1,
        daysPerRound: 14,
        leagueEndDate: '',
        maxFreezes: 2,
        frozenCompensationPct: 0.7,
    });
    const [groups, setGroups] = useState([]);
    const [groupAssignments, setGroupAssignments] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [modalMessage, setModalMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newEndDate, setNewEndDate] = useState('');
    const [addPlayerEmail, setAddPlayerEmail] = useState('');
    const [leagueMembers, setLeagueMembers] = useState([]);
    const [isPositiveModal, setIsPositiveModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmLabel, setConfirmLabel] = useState('');

    useEffect(() => {
        loadData();
    }, [tournamentId]);

    const loadData = async () => {
        setIsLoading(true);
        const tournamentResult = await fetchTournament(tournamentId);
        if (tournamentResult.success) {
            setTournament(tournamentResult.data);
        }

        const participantsResult = await fetchParticipants(tournamentId);
        if (participantsResult.success) {
            setParticipants(participantsResult.data);
        }

        const configResult = await fetchLeagueConfig(tournamentId);
        if (configResult.success && configResult.data) {
            setConfig(configResult.data);
            if (configResult.data.currentRoundEndDate) {
                setNewEndDate(configResult.data.currentRoundEndDate.substring(0, 16));
            }
        }

        // Load existing league view (groups + assignments) if league is active
        const leagueViewResult = await fetchLeagueView(tournamentId, null);
        if (leagueViewResult.success && leagueViewResult.data) {
            const viewData = leagueViewResult.data;
            if (viewData.groupStandings && viewData.groupStandings.length > 0) {
                const loadedGroups = viewData.groupStandings.map((gs, i) => ({
                    groupLabel: gs.groupLabel,
                    weight: gs.weight,
                    groupPremium: gs.groupPremium ?? 0,
                    placeBonusStep: gs.placeBonusStep ?? 0.05,
                    displayOrder: i,
                }));
                setGroups(loadedGroups);

                // Build assignments from standings (current players in each group)
                const loadedAssignments = {};
                viewData.groupStandings.forEach(gs => {
                    loadedAssignments[gs.groupLabel] = gs.standings.map(s => s.participantId);
                });
                setGroupAssignments(loadedAssignments);
            }
        }

        setIsLoading(false);
    };

    const handleSaveConfig = async () => {
        const result = await saveLeagueConfig(tournamentId, config);
        if (result.success) {
            setConfig(result.data);
            showModal('Konfiguracja zapisana', true);
        } else {
            showModal('Błąd: ' + result.data);
        }
    };

    const handleAddGroup = () => {
        const label = String.fromCharCode(65 + groups.length); // A, B, C...
        // Default premium scale (linear, step -5): A=25, B=20, C=15, D=10, E=5, F+=0
        const defaultPremiums = [25, 20, 15, 10, 5];
        const defaultPremium = defaultPremiums[groups.length] ?? 0;
        setGroups([...groups, {
            groupLabel: label,
            weight: 1.0 - groups.length * 0.2,
            groupPremium: defaultPremium,
            placeBonusStep: 0.05,
            members: []
        }]);
        setGroupAssignments({ ...groupAssignments, [label]: [] });
    };

    const handleRemoveGroup = (index) => {
        const removed = groups[index];
        const newGroups = groups.filter((_, i) => i !== index);
        const newAssignments = { ...groupAssignments };
        delete newAssignments[removed.groupLabel];
        setGroups(newGroups);
        setGroupAssignments(newAssignments);
    };

    const handleWeightChange = (index, weight) => {
        const newGroups = [...groups];
        newGroups[index].weight = parseFloat(weight) || 0;
        setGroups(newGroups);
    };

    const handleGroupPremiumChange = (index, value) => {
        const newGroups = [...groups];
        newGroups[index].groupPremium = parseFloat(value) || 0;
        setGroups(newGroups);
    };

    const handlePlaceBonusStepChange = (index, valuePct) => {
        // UI shows percent (e.g. "5"), entity stores fraction (0.05)
        const newGroups = [...groups];
        newGroups[index].placeBonusStep = (parseFloat(valuePct) || 0) / 100;
        setGroups(newGroups);
    };

    const handleAssignPlayer = (groupLabel, participantId) => {
        // Remove from any other group
        const newAssignments = {};
        for (const [label, ids] of Object.entries(groupAssignments)) {
            newAssignments[label] = ids.filter(id => id !== participantId);
        }
        newAssignments[groupLabel] = [...(newAssignments[groupLabel] || []), participantId];
        setGroupAssignments(newAssignments);
    };

    const handleRemovePlayer = (groupLabel, participantId) => {
        const newAssignments = { ...groupAssignments };
        newAssignments[groupLabel] = newAssignments[groupLabel].filter(id => id !== participantId);
        setGroupAssignments(newAssignments);
    };

    const handleSaveGroupsAndAssignments = async () => {
        // 1. Zapisz strukturę grup (etykiety + wagi + premia + bonus miejsca)
        const groupDtos = groups.map((g, i) => ({
            groupLabel: g.groupLabel,
            weight: g.weight,
            groupPremium: g.groupPremium ?? 0,
            placeBonusStep: g.placeBonusStep ?? 0.05,
            displayOrder: i,
        }));
        const groupResult = await saveLeagueGroups(tournamentId, groupDtos);
        if (!groupResult.success) {
            showModal('Błąd zapisu grup: ' + groupResult.data);
            return;
        }

        // 2. Jeśli liga aktywna — użyj modifyGroups (kasuje mecze + przypisuje + regeneruje)
        const roundNumber = config.currentRoundNumber || 1;

        if (isLeagueStarted) {
            // Sprawdź czy w bieżącej rundzie są wyniki
            const viewResult = await fetchLeagueView(tournamentId, roundNumber);
            if (viewResult.success && viewResult.data) {
                const hasResults = viewResult.data.groupStandings.some(gs =>
                    gs.matches.some(m => m.participantWinnerId !== null)
                );
                if (hasResults) {
                    showModal('Nie można zmienić składu grup — w bieżącej rundzie są już wpisane wyniki.');
                    return;
                }
            }

            const modifyResult = await modifyLeagueGroups(tournamentId, roundNumber, groupAssignments);
            if (modifyResult.success) {
                showModal('Grupy zapisane, mecze wygenerowane ponownie', true);
                loadData();
            } else {
                showModal('Błąd: ' + modifyResult.data);
            }
        } else {
            // Liga jeszcze nie wystartowała — tylko przypisz
            const assignResult = await assignPlayersToGroups(tournamentId, roundNumber, groupAssignments);
            if (assignResult.success) {
                showModal('Grupy i przypisania graczy zapisane', true);
            } else {
                showModal('Błąd przypisania graczy: ' + assignResult.data);
            }
        }
    };

    const handleStartLeague = async () => {
        const result = await startLeague(tournamentId);
        if (result.success) {
            showModal('Liga wystartowana!', true);
            loadData();
        } else {
            showModal('Błąd: ' + result.data);
        }
    };

    const handleAdvanceRound = () => {
        setConfirmAction(() => async () => {
            const result = await advanceLeagueRound(tournamentId);
            if (result.success) {
                showModal('Przejście do nowej rundy', true);
                loadData();
            } else {
                showModal('Błąd: ' + result.data);
            }
        });
        setConfirmLabel('Tak, przejdź');
        setModalMessage('Czy na pewno chcesz przejść do następnej rundy? Zostaną wykonane awanse i spadki graczy.');
        setIsPositiveModal(false);
        setIsModalOpen(true);
    };

    const handleRollbackRound = () => {
        if (config.currentRoundNumber <= 1) {
            showModal('Nie można cofnąć — to jest pierwsza runda');
            return;
        }
        setConfirmAction(() => async () => {
            const result = await rollbackLeagueRound(tournamentId);
            if (result.success) {
                showModal('Cofnięto do rundy ' + (config.currentRoundNumber - 1), true);
                loadData();
            } else {
                showModal('Błąd: ' + result.data);
            }
        });
        setConfirmLabel('Tak, cofnij');
        setModalMessage(`Czy na pewno chcesz usunąć rundę ${config.currentRoundNumber} i cofnąć się do rundy ${config.currentRoundNumber - 1}? Wszystkie mecze bieżącej rundy zostaną usunięte.`);
        setIsPositiveModal(false);
        setIsModalOpen(true);
    };

    const handleConfirm = async () => {
        setIsModalOpen(false);
        if (confirmAction) {
            await confirmAction();
            setConfirmAction(null);
            setConfirmLabel('');
        }
    };

    const handleChangeEndDate = async () => {
        if (!newEndDate) return;
        const result = await changeLeagueRoundEndDate(tournamentId, newEndDate);
        if (result.success) {
            showModal('Data zakończenia rundy zmieniona', true);
            loadData();
        } else {
            showModal('Błąd: ' + result.data);
        }
    };

    const handleAddPlayerByEmail = async () => {
        if (!addPlayerEmail) {
            showModal('Podaj email gracza');
            return;
        }

        // Sprawdź czy gracz już jest w jakiejś grupie
        const allAssignedIds = new Set(Object.values(groupAssignments).flat());
        const existingParticipant = participants.find(
            p => (p.email?.toLowerCase() === addPlayerEmail.toLowerCase() ||
                  p.username?.toLowerCase() === addPlayerEmail.toLowerCase())
        );
        if (existingParticipant && allAssignedIds.has(existingParticipant.id)) {
            const groupLabel = Object.entries(groupAssignments).find(
                ([, ids]) => ids.includes(existingParticipant.id)
            )?.[0];
            showModal(`Gracz ${addPlayerEmail} jest już przypisany do grupy ${groupLabel || '?'}`);
            return;
        }

        // Zarejestruj gracza w turnieju
        const registerResult = await registerTournamentParticipantBySupervisor(tournament.id, addPlayerEmail, '');
        if (!registerResult.success) {
            showModal('Błąd rejestracji: ' + (registerResult.data || 'Nieznany błąd'));
            return;
        }

        showModal('Gracz ' + addPlayerEmail + ' dodany do turnieju. Przypisz go do grupy w sekcji Grupy.', true);
        setAddPlayerEmail('');
        loadData();
    };

    const handleModifyGroups = async () => {
        const roundNumber = config.currentRoundNumber || 1;

        // Sprawdź czy w bieżącej rundzie są wpisane wyniki
        const viewResult = await fetchLeagueView(tournamentId, roundNumber);
        if (viewResult.success && viewResult.data) {
            const hasResults = viewResult.data.groupStandings.some(gs =>
                gs.matches.some(m => m.participantWinnerId !== null)
            );
            if (hasResults) {
                showModal('Nie można przegenerować meczów — w bieżącej rundzie są już wpisane wyniki. Usuń wyniki przed modyfikacją grup.');
                return;
            }
        }

        const result = await modifyLeagueGroups(tournamentId, roundNumber, groupAssignments);
        if (result.success) {
            showModal('Skład grup zmieniony, mecze wygenerowane ponownie', true);
            loadData();
        } else {
            showModal('Błąd: ' + result.data);
        }
    };

    const handleSendReminders = async () => {
        const result = await sendLeagueActivityReminders(tournamentId);
        if (result.success) {
            showModal(`Wysłano ${result.data.sent} przypomnień`, true);
        } else {
            showModal('Błąd: ' + (result.data || ''));
        }
    };

    const handleAdminFreeze = async (participantId) => {
        const result = await adminFreezePlayer(tournamentId, participantId);
        if (result.success) {
            showModal('Zamrożenie zgłoszone', true);
            loadData();
        } else {
            showModal('Błąd: ' + (result.data || ''));
        }
    };

    const handleAdminUnfreeze = async (participantId) => {
        const result = await adminUnfreezePlayer(tournamentId, participantId);
        if (result.success) {
            showModal('Odmrożenie wykonane', true);
            loadData();
        } else {
            showModal('Błąd: ' + (result.data || ''));
        }
    };

    const showModal = (msg, positive = false) => {
        setModalMessage(msg);
        setIsPositiveModal(positive);
        setConfirmAction(null);
        setConfirmLabel('');
        setIsModalOpen(true);
    };

    const getUnassignedParticipants = () => {
        const assignedIds = new Set(Object.values(groupAssignments).flat());
        return participants.filter(p => !assignedIds.has(p.id));
    };

    const getParticipantName = (participantId) => {
        const p = participants.find(pp => pp.id === participantId);
        return p ? `${p.firstName} ${p.lastName}` : `ID: ${participantId}`;
    };

    if (isLoading) return <Spinner />;

    const isLeagueStarted = tournament && tournament.status === 'ACTIVE';

    const formatDateTime = (dt) => {
        if (!dt) return '-';
        return new Date(dt).toLocaleString('pl-PL', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>
                Zarządzanie Ligą: {tournament?.title || ''}
            </h2>

            {isLeagueStarted && config.currentRoundNumber > 0 && (
                <section className={styles.section}>
                    <h3>Status ligi</h3>
                    <div className={styles.statusGrid}>
                        <div><strong>Aktualna runda:</strong> {config.currentRoundNumber}</div>
                        <div><strong>Początek rundy:</strong> {formatDateTime(config.currentRoundStartDate)}</div>
                        <div><strong>Koniec rundy:</strong> {formatDateTime(config.currentRoundEndDate)}</div>
                        <div><strong>Początek ligi:</strong> {formatDateTime(tournament?.tournamentDate)}</div>
                        <div><strong>Koniec ligi:</strong> {config.leagueEndDate ? formatDateTime(config.leagueEndDate) : 'Nie ustawiono'}</div>
                    </div>
                </section>
            )}

            {/* KONFIGURACJA */}
            <section className={styles.section}>
                <h3>Konfiguracja Ligi</h3>
                <div className={styles.formGrid}>
                    <label>
                        System punktacji:
                        <select value={config.scoringSystem} onChange={e => setConfig({ ...config, scoringSystem: e.target.value })}>
                            <option value="CLASSIC">Klasyczny (2:0)</option>
                            <option value="SYSTEM_3210">System 3-2-1-0</option>
                        </select>
                    </label>
                    <label>
                        Awansuje:
                        <input type="number" min="0" max="3" value={config.promotedPerGroup}
                               onChange={e => setConfig({ ...config, promotedPerGroup: parseInt(e.target.value) || 0 })} />
                    </label>
                    <label>
                        Spada:
                        <input type="number" min="0" max="3" value={config.relegatedPerGroup}
                               onChange={e => setConfig({ ...config, relegatedPerGroup: parseInt(e.target.value) || 0 })} />
                    </label>
                    <label>
                        Dni na rundę:
                        <input type="number" min="1" max="60" value={config.daysPerRound}
                               onChange={e => setConfig({ ...config, daysPerRound: parseInt(e.target.value) || 7 })} />
                    </label>
                    <label>
                        Maks. zamrożeń na sezon:
                        <input type="number" min="0" max="10" value={config.maxFreezes}
                               onChange={e => setConfig({ ...config, maxFreezes: parseInt(e.target.value) || 0 })} />
                    </label>
                    <label title="Procent średniej rundy gracza przyznawany jako kompensacja za każdą zamrożoną rundę (0 = bez kompensacji, 100 = pełna kompensacja, 70 = rekomendowane)">
                        Kompensacja zamrożenia (%):
                        <input type="number" min="0" max="100" step="5"
                               value={Math.round((config.frozenCompensationPct ?? 0) * 100)}
                               onChange={e => setConfig({ ...config, frozenCompensationPct: (parseInt(e.target.value) || 0) / 100 })} />
                    </label>
                    <label>
                        Data zakończenia ligi:
                        <input type="datetime-local" value={config.leagueEndDate || ''}
                               onChange={e => setConfig({ ...config, leagueEndDate: e.target.value })} />
                    </label>
                </div>
                <button className={styles.saveButton} onClick={handleSaveConfig}>Zapisz konfigurację</button>
            </section>

            {/* GRUPY */}
            <section className={styles.section}>
                <h3>Grupy i punktacja</h3>
                {groups.map((group, index) => (
                    <div key={group.groupLabel} className={styles.groupBlock}>
                        <div className={styles.groupHeader}>
                            <strong>Grupa {group.groupLabel}</strong>
                            <label title="Stała baza punktowa za udział w grupie, proporcjonalna do rozegranych/zaplanowanych meczów">
                                Premia grupowa:
                                <input type="number" step="1" min="0" value={group.groupPremium ?? 0}
                                       onChange={e => handleGroupPremiumChange(index, e.target.value)}
                                       className={styles.weightInput} />
                            </label>
                            <label title="Krok % premii za miejsce w grupie (0 = wyłączone). Przedostatnie miejsce +step%, wyższe rosnąco.">
                                Bonus miejsca (%):
                                <input type="number" step="1" min="0" max="50"
                                       value={Math.round((group.placeBonusStep ?? 0) * 100)}
                                       onChange={e => handlePlaceBonusStepChange(index, e.target.value)}
                                       className={styles.weightInput} />
                            </label>
                            <button className={styles.removeButton} onClick={() => handleRemoveGroup(index)}>Usuń</button>
                        </div>
                        <div className={styles.groupPlayers}>
                            {(groupAssignments[group.groupLabel] || []).map(pid => (
                                <div key={pid} className={styles.playerChip}>
                                    {getParticipantName(pid)}
                                    <button onClick={() => handleRemovePlayer(group.groupLabel, pid)}>×</button>
                                </div>
                            ))}
                            <select onChange={e => {
                                if (e.target.value) {
                                    handleAssignPlayer(group.groupLabel, parseInt(e.target.value));
                                    e.target.value = '';
                                }
                            }}>
                                <option value="">+ Dodaj gracza</option>
                                {getUnassignedParticipants().map(p => (
                                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                ))}
                <button className={styles.addGroupButton} onClick={handleAddGroup}>+ Dodaj grupę</button>
                <div className={styles.buttonRow}>
                    <button className={styles.saveButton} onClick={handleSaveGroupsAndAssignments}>Zapisz grupy i przypisania</button>
                </div>
            </section>

            {/* AKCJE */}
            <section className={styles.section}>
                <h3>Akcje</h3>
                <div className={styles.actionsGrid}>
                    {!isLeagueStarted && (
                        <button className={styles.startButton} onClick={handleStartLeague}>Wystartuj ligę</button>
                    )}
                    {isLeagueStarted && (
                        <>
                            <button className={styles.actionButton} onClick={handleAdvanceRound}>Następna runda</button>
                            <button className={styles.dangerButton} onClick={handleRollbackRound}>Cofnij rundę</button>
                            <div className={styles.actionRow}>
                                <input type="datetime-local" value={newEndDate}
                                       onChange={e => setNewEndDate(e.target.value)} />
                                <button className={styles.actionButton} onClick={handleChangeEndDate}>
                                    Zmień datę końca rundy
                                </button>
                            </div>
                            <div className={styles.actionRow}>
                                <input
                                    type="email"
                                    placeholder="Email gracza"
                                    value={addPlayerEmail}
                                    onChange={e => setAddPlayerEmail(e.target.value)}
                                    className={styles.emailInput}
                                />
                                <button className={styles.actionButton} onClick={handleAddPlayerByEmail}>Dodaj gracza do turnieju</button>
                            </div>
                            <button className={styles.actionButton} onClick={handleSendReminders}>Generuj przypomnienia</button>
                        </>
                    )}
                </div>
            </section>

            {/* ZAMROŻENIA */}
            {isLeagueStarted && (
                <section className={styles.section}>
                    <h3>Zamrożenia graczy</h3>
                    <p className={styles.freezeInfo}>
                        Limit zamrożeń na sezon: <strong>{config.maxFreezes}</strong>
                    </p>
                    <table className={styles.freezeTable}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Gracz</th>
                                <th>Wykorzystane</th>
                                <th>Status</th>
                                <th>Akcja</th>
                                <th>Piłki</th>
                            </tr>
                        </thead>
                        <tbody>
                            {participants.map((p, index) => (
                                <tr key={p.id}>
                                    <td>{index + 1}</td>
                                    <td>{p.firstName} {p.lastName}</td>
                                    <td>{p.freezesUsed || 0} / {config.maxFreezes}</td>
                                    <td>
                                        {p.frozenForNextRound
                                            ? <span className={styles.frozenBadge}>Zamrożony (nast. runda)</span>
                                            : <span className={styles.activeBadge}>Aktywny</span>
                                        }
                                    </td>
                                    <td>
                                        {p.frozenForNextRound ? (
                                            <button className={styles.unfreezeSmallButton} onClick={() => handleAdminUnfreeze(p.id)}>
                                                Odmroź
                                            </button>
                                        ) : (
                                            <button className={styles.freezeSmallButton} onClick={() => handleAdminFreeze(p.id)}>
                                                Zamroź
                                            </button>
                                        )}
                                    </td>
                                    <td>
                                        {(() => {
                                            const provided = p.ballsProvided || 0;
                                            const played = p.matchesPlayedTotal || 0;
                                            const pct = played > 0 ? Math.round((provided / played) * 100) : 0;
                                            return `${provided}/${played}\u00A0\u00A0\u00A0${pct}%`;
                                        })()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={() => { setIsModalOpen(false); setConfirmAction(null); setConfirmLabel(''); }}
                message={modalMessage}
                isPositiveMessage={isPositiveModal}
                cancelLabel="Zamknij"
                cancelButtonColor={isPositiveModal ? 'green' : undefined}
                showConfirmButton={!!confirmAction}
                confirmLabel={confirmLabel}
                onConfirm={handleConfirm}
                confirmButtonColor="red"
            />
        </div>
    );
};

export default AdminLeagueManagement;
